// voice-call-new.component.ts (UPDATED)

import {
    Component,
    computed,
    DestroyRef,
    inject,
    Input,
    OnDestroy,
    OnInit,
    signal,
    Signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { VoiceService, SipService } from '@dxp/core/services';
import { TelXLDropdownComponent, TelXLDialpadComponent } from '@dxp/elements';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import {
    IContactSelected,
    IDropdown,
    IInternalHistoricalWorkItems,
} from '@dxp/core/models';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { filter, delay } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { VoiceAudioComponent } from '../voice-audio/voice-audio.component';
import { VoiceRecentCallComponent } from '../voice-recent-call/voice-recent-call.component';
import { VoiceCallNewService } from '../../services/voice-call-new.service';

@Component({
    selector: 'voice-call-new',
    standalone: true,
    imports: [
        CommonModule,
        TelXLDropdownComponent,
        TelXLDialpadComponent,
        VoiceRecentCallComponent,
        NgbTooltip,
        VoiceAudioComponent,
    ],
    templateUrl: './voice-call-new.component.html',
    styleUrl: './voice-call-new.component.scss',
})
export class VoiceCallNewComponent implements OnInit, OnDestroy {
    readonly #voiceCallNewService = inject(VoiceCallNewService);
    readonly #voiceService = inject(VoiceService);
    readonly #route = inject(ActivatedRoute);
    readonly #sipService = inject(SipService);
    readonly #destroyRef = inject(DestroyRef);

    private clearTimer: any = null;
    readonly showTerminalMessage = signal(false);

    @Input()
    set contactSelected(value: IContactSelected | null) {
        if (value?.internalCall) {
            this.#voiceCallNewService.callNumber.set(value.name);
        } else if (value?.identifier) {
            this.#voiceCallNewService.callNumber.set(value.identifier);
        }
    }

    readonly callNumber: Signal<string> = this.#voiceCallNewService.callNumber;
    readonly canCall: Signal<boolean> = this.#voiceCallNewService.canCall;
    readonly startedCall: Signal<boolean> =
        this.#voiceCallNewService.startedCall;
    readonly callState: Signal<string> = this.#voiceCallNewService.callState;
    readonly callToolTip: Signal<string> =
        this.#voiceCallNewService.callToolTip;
    readonly displayFailed: Signal<boolean> =
        this.#voiceCallNewService.displayFailed;
    readonly dialpad: Signal<boolean> = this.#voiceCallNewService.dialpad;
    readonly recentCalls: Signal<IInternalHistoricalWorkItems | null> =
        this.#voiceCallNewService.recentCalls;
    readonly costCentreId: Signal<string> =
        this.#voiceCallNewService.costCentreId;
    readonly overrideWithheld: Signal<boolean> =
        this.#voiceCallNewService.overrideWithheld;
    readonly opCLI: Signal<string | null> = this.#voiceCallNewService.opCLI;
    readonly costCentresData: Signal<IDropdown<string>[]> =
        this.#voiceCallNewService.costCentresData;
    readonly connecting: Signal<boolean> = this.#voiceCallNewService.connecting;
    readonly audioOpen = signal(false);
    readonly callFromContact = signal(false);

    readonly callDisplay = computed(() => {
        if (this.showTerminalMessage()) {
            return this.callState();
        }

        return this.displayFailed()
            ? this.callState()
            : `${this.callNumber()}${this.callState() ? ' ' + this.callState() : ''}`;
    });

    readonly callButtonClass = computed(() =>
        this.startedCall()
            ? 'btn-reject'
            : this.canCall()
              ? 'btn-call'
              : 'btn-call-disabled',
    );

    readonly showDialpadButton = computed(
        () => !this.displayFailed() && !this.startedCall(),
    );

    disabledCostCentreForInternal = computed(() =>
        this.#voiceCallNewService.disableCostCentersForInternalCall(),
    );

    failedCall$ = toObservable(this.callState).pipe(
        takeUntilDestroyed(),
        filter(
            callState =>
                callState === 'Call failed' ||
                callState === 'Number busy' ||
                callState === 'Agent rejected' ||
                callState === 'Agent unavailable',
        ),
    );

    endedCall$ = toObservable(this.#sipService.callState).pipe(
        takeUntilDestroyed(),
        filter(state => state === 'ended'),
        delay(1000),
    );

    ngOnInit(): void {
        const encoded = this.#route.snapshot.paramMap.get('number');
        if (encoded) {
            try {
                const number = atob(encoded);
                this.#voiceCallNewService.callNumber.set(number);
                this.callFromContact.set(true);
            } catch {
                console.error('Invalid number encoding');
            }
        }

        this.#voiceCallNewService.refreshData();
        this.#voiceService.setContacts(true, '', 'voice', undefined, false);

        this.failedCall$.subscribe(() => {
            this.showTerminalMessage.set(true);
            this.#voiceCallNewService.handleTerminalEnd();
            this.#voiceCallNewService.redialBlocked.set(true);
            this.#sipService.hangUp();
        });

        this.failedCall$
            .pipe(delay(5000), takeUntilDestroyed(this.#destroyRef))
            .subscribe(() => {
                this.#voiceCallNewService.clearAfterTerminal();
                this.#voiceCallNewService.redialBlocked.set(false);
                this.showTerminalMessage.set(false);
            });

        this.endedCall$.subscribe(() => {
            const s = this.callState();

            if (this.callFromContact()) return;
            if (
                s === 'Call failed' ||
                s === 'Number busy' ||
                s === 'Agent rejected' ||
                s === 'Agent unavailable'
            ) {
                this.#voiceCallNewService.handleTerminalEnd();
                return;
            }

            this.#voiceCallNewService.endCall();
            this.#voiceCallNewService.reset();
        });
    }

    ngOnDestroy(): void {
        clearTimeout(this.clearTimer);
        this.#voiceCallNewService.reset();
        this.#voiceService.contacts().display = false;
    }

    withholdClick(checked: boolean): void {
        this.#voiceCallNewService.setWithholdCLI(checked);
    }

    onNumberChange(event: Event): void {
        this.#voiceCallNewService.updateNumber(
            (event.target as HTMLInputElement).value,
        );
    }

    makeCall(tooltip: NgbTooltip): void {
        tooltip.close();
        this.#voiceCallNewService.makeCall();
    }

    endCall(tooltip: NgbTooltip): void {
        tooltip.close();
        this.#voiceCallNewService.endCall();
    }

    showDialpad(): void {
        this.#voiceCallNewService.toggleDialpad();
    }

    dialpadClick(number: string): void {
        this.#voiceCallNewService.dialpadClick(number);
    }

    onCostCentreChange(costCentreId: string): void {
        this.#voiceCallNewService.onCostCentreChange(costCentreId);
    }

    onContactSelected(contact: IContactSelected): void {
        this.#voiceCallNewService.callNumber.set(contact.identifier ?? '');
    }

    callSelected(contact: IContactSelected, id: string): void {
        this.#voiceService.contactSelected(contact);
        this.#voiceService.setContactCardSelected(id);
        this.#voiceCallNewService.callNumber.set(contact.identifier ?? '');

        /**
         * Resets the internal cost centre - When click on from recent calls
         */
        this.#voiceCallNewService.resetInternalCostCentre();
    }

    isSelected(id: string): boolean {
        return this.#voiceService.getContactCardSelected() === id;
    }

    showAudio() {
        this.audioOpen.set(!this.audioOpen());
    }
}
